import React, { useState, useEffect, useRef } from "react";
import SingleProductDetails from "../components/SingleProductDetails/SingleProductDetails";
import { getproductDetails } from "../appState/slices/product/productSlice";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Modal from "../components/ModalImage/Modal";

const SingleProduct = () => {
  const [colourforimg, setColorforImg] = useState("");
  const [windowSize, setWindowSize] = useState(getWindowSize());
  const [topcontainerheight, setTopContainerHeight] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [minbottomheight, setMinBottomHeight] = useState(0);
  const [startY, setStartY] = useState(0);
  const [currentTopContainerHeight, setCurrentTopContainerHeight] = useState(0);
  const [scrollEnabled, setScrollEnabled] = useState(false);
  const [dragdirection, setDragDirection] = useState("");
  const [isAtTop, setIsAtTop] = useState(true);

  const params = useParams();
  const { id: productId } = params;
  const productDetail = useSelector((state) => state.product.productDetail) || {};
  const dispatch = useDispatch();

  const scrollRef = useRef(null);
  const imageScrollRef = useRef(null); // ✅ NEW

  /** 🟢 Set color */
  function setImageColor(selectedcolor) {
    setColorforImg(selectedcolor);
  }

  /** 🟢 Fetch product */
  useEffect(() => {
    dispatch(getproductDetails(productId));
  }, [productId, dispatch]);

  /** 🟢 Window size */
  function getWindowSize() {
    return {
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight,
    };
  }

  useEffect(() => {
    const handleResize = () => setWindowSize(getWindowSize());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /** 🟢 Track bottom scroll position */
  useEffect(() => {
    const handleScroll = () => {
      if (scrollRef.current) {
        setIsAtTop(scrollRef.current.scrollTop === 0);
      }
    };

    const el = scrollRef.current;
    if (el) el.addEventListener("scroll", handleScroll);

    return () => {
      if (el) el.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /** 🟢 🔥 NEW: Detect image scroll end */
  useEffect(() => {
    const handleImageScroll = () => {
      const el = imageScrollRef.current;
      if (!el) return;

      const isAtBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - 5;

      if (isAtBottom) {
        const maxHeight = windowSize.innerHeight - 150;

        setCurrentTopContainerHeight(maxHeight);
        setScrollEnabled(true);
      }
    };

    const el = imageScrollRef.current;
    if (el) el.addEventListener("scroll", handleImageScroll);

    return () => {
      if (el) el.removeEventListener("scroll", handleImageScroll);
    };
  }, [windowSize.innerHeight]);

  /** 🟢 Touch Start */
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
  };

  /** 🟢 Touch Move */
  const handleTouchMove = (e) => {
    if (!isDragging) return;

    const deltaY = e.touches[0].clientY - startY;
    const minHeight = minbottomheight;
    const maxHeight = windowSize.innerHeight - 150;
    const newHeight = currentTopContainerHeight - deltaY;

    // 🔼 DRAG UP
    if (deltaY < 0) {
      if (newHeight >= minHeight && newHeight <= maxHeight) {
        setCurrentTopContainerHeight(newHeight);
        setDragDirection("up");
        setStartY(e.touches[0].clientY);
      }
    }

    // 🔽 DRAG DOWN (ONLY IF SCROLL AT TOP)
    if (deltaY > 0 && isAtTop) {
      if (newHeight >= minHeight && newHeight <= maxHeight) {
        setCurrentTopContainerHeight(newHeight);
        setDragDirection("down");
        setStartY(e.touches[0].clientY);
      }
    }
  };

  /** 🟢 Touch End */
  const handleTouchEnd = () => {
    const minHeight = minbottomheight;
    const maxHeight = windowSize.innerHeight - 150;
    const threshold = 100;

    if (dragdirection === "up") {
      setCurrentTopContainerHeight(maxHeight);
      setScrollEnabled(true);
    } else if (dragdirection === "down") {
      if (currentTopContainerHeight < maxHeight - threshold) {
        setCurrentTopContainerHeight(minHeight);
        setScrollEnabled(false);
      } else {
        setCurrentTopContainerHeight(maxHeight);
      }
    }

    setIsDragging(false);
  };

  /** 🟢 Initial layout */
  useEffect(() => {
    const topHeight = (70 * windowSize.innerHeight) / 100;
    const bottomHeight = windowSize.innerHeight - topHeight - 75;

    setTopContainerHeight(topHeight);
    setCurrentTopContainerHeight(bottomHeight);
    setMinBottomHeight(bottomHeight);
  }, [windowSize.innerHeight]);

  /** 🟡 Get images */
  const getImagesForColor = (color) => {
    if (!productDetail || !productDetail.coloredImages) return [];

    const colorData = productDetail.coloredImages.find(
      (img) => img.color?.toLowerCase() === color?.toLowerCase()
    );

    const allImages = productDetail.coloredImages.find(
      (img) => img.appliesToAll === true
    );

    if (colorData?.images?.length) return colorData.images;
    if (allImages?.images?.length) return allImages.images;
    if (productDetail.defaultImageUrl) return [productDetail.defaultImageUrl];

    return [];
  };

  const activeImages = getImagesForColor(colourforimg);

  return (
    <>
      {windowSize.innerWidth >= 767 ? (
        <div className="single-product">
          {Object.keys(productDetail).length ? (
            <>
              <div className="left-side-singleproduct">
                {activeImages?.length ? (
                  activeImages.map((image, idx) => (
                    <Modal key={idx} image={image} />
                  ))
                ) : (
                  <Modal image={productDetail.defaultImageUrl} />
                )}
              </div>

              <div className="right-side-singleproduct">
                <SingleProductDetails
                  setImageColor={setImageColor}
                  productDetail={productDetail}
                />
              </div>
            </>
          ) : null}
        </div>
      ) : (
        <div className="mobile-single-product">
          {Object.keys(productDetail).length ? (
            <>
              {/* 🔥 IMAGES (SCROLLABLE) */}
              <div
                ref={imageScrollRef}
                className="mobile-top-singleproduct"
                style={{
                  height: `${topcontainerheight}px`,
                  overflowY: "auto",
                }}
              >
                {activeImages?.length ? (
                  activeImages.map((image, idx) => (
                    <Modal key={idx} image={image} />
                  ))
                ) : (
                  <Modal image={productDetail.defaultImageUrl} />
                )}
              </div>

              {/* 🔥 BOTTOM DRAG PANEL */}
              <div
                className="mobile-bottom-singleproduct"
                ref={scrollRef}
                style={{
                  height: `${currentTopContainerHeight}px`,
                  overflow: scrollEnabled ? "auto" : "hidden",
                  transition: "height 0.25s ease",
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <center>
                  <div className="draggable"></div>
                </center>

                <SingleProductDetails
                  setImageColor={setImageColor}
                  productDetail={productDetail}
                />
              </div>
            </>
          ) : null}
        </div>
      )}
    </>
  );
};

export default SingleProduct;
